import type {
  PromptTemplateCreatePayload,
  PromptTemplateUpdatePayload,
} from '@saaga/shared/interfaces/prompt-template.interface'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { toast } from 'sonner'

import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'

type MutationTrigger<TArg, TResult> = (arg: TArg) => { unwrap: () => Promise<TResult> }

interface SaveTemplateParams {
  values: PromptTemplateDrawerFormSchemaType
  templateId: string | null
  agencySlug?: string
  createFn: MutationTrigger<PromptTemplateCreatePayload, unknown>
  updateFn: MutationTrigger<{ id: string; payload: PromptTemplateUpdatePayload }, unknown>
}

export const savePromptTemplate = async ({
  values,
  templateId,
  agencySlug,
  createFn,
  updateFn,
}: SaveTemplateParams): Promise<boolean> => {
  try {
    const payload = { name: values.name, content: values.content, description: values.description }
    if (templateId) {
      await updateFn({ id: templateId, payload }).unwrap()
      trackEvent('prompt_template_updated', { templateName: values.name })
      toast.success('Template updated')
      return true
    }
    await createFn({ ...payload, agency_slug: agencySlug }).unwrap()
    trackEvent('prompt_template_created', { templateName: values.name })
    toast.success('Template created')
    return true
  } catch (error) {
    logger.error('Failed to save template', error)
    toast.error(templateId ? 'Failed to update template' : 'Failed to create template')
    return false
  }
}

export const deletePromptTemplate = async (
  id: string,
  deleteFn: MutationTrigger<string, unknown>
): Promise<void> => {
  try {
    await deleteFn(id).unwrap()
    trackEvent('prompt_template_deleted', {})
    toast.success('Template deleted')
  } catch (error) {
    logger.error('Failed to delete template', error)
    toast.error('Failed to delete template')
  }
}
